import React, { createContext, useContext, useCallback, useState } from 'react';
import ToastContainer, { type ToastData } from '../components/Toast/ToastContainer';

interface NotificationContextType {
  showToast: (toast: Omit<ToastData, 'id'>) => string;
  showSuccess: (title: string, message: string, options?: { duration?: number; icon?: string }) => string;
  showError: (title: string, message: string, options?: { duration?: number; icon?: string }) => string;
  showWarning: (title: string, message: string, options?: { duration?: number; icon?: string }) => string;
  showInfo: (title: string, message: string, options?: { duration?: number; icon?: string }) => string;
  showGameNotification: (title: string, message: string, options?: { duration?: number; icon?: string }) => string;
  showConnectionAlert: (title: string, message: string, options?: { duration?: number; icon?: string }) => string;
  removeToast: (id: string) => void;
  clearAllToasts: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: React.ReactNode;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
  maxToasts?: number;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
  position = 'top-left', // Changed from 'top-right' for better mobile UX
  maxToasts = 3 // Reduced from 5 for compact design
}) => {
  const [toasts, setToasts] = useState<ToastData[]>([]);

  const generateId = useCallback(() => {
    return `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  const showToast = useCallback((toast: Omit<ToastData, 'id'>) => {
    const id = generateId();
    const newToast: ToastData = { ...toast, id };

    setToasts(prev => {
      const updated = [newToast, ...prev];
      // Limit the number of toasts
      return updated.slice(0, maxToasts);
    });

    return id;
  }, [generateId, maxToasts]);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const clearAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  // Convenience methods for different toast types with optimized durations
  const showSuccess = useCallback((title: string, message: string, options?: { duration?: number; icon?: string }) => {
    return showToast({
      type: 'success',
      title,
      message,
      duration: options?.duration || 3500, // Success: 3.5s (reduced from 4s)
      icon: options?.icon
    });
  }, [showToast]);

  const showError = useCallback((title: string, message: string, options?: { duration?: number; icon?: string }) => {
    return showToast({
      type: 'error',
      title,
      message,
      duration: options?.duration || 5500, // Errors: 5.5s (reduced from 6s)
      icon: options?.icon
    });
  }, [showToast]);

  const showWarning = useCallback((title: string, message: string, options?: { duration?: number; icon?: string }) => {
    return showToast({
      type: 'warning',
      title,
      message,
      duration: options?.duration || 4500, // Warnings: 4.5s (reduced from 5s)
      icon: options?.icon
    });
  }, [showToast]);

  const showInfo = useCallback((title: string, message: string, options?: { duration?: number; icon?: string }) => {
    return showToast({
      type: 'info',
      title,
      message,
      duration: options?.duration || 3500, // Info: 3.5s (reduced from 4s)
      icon: options?.icon
    });
  }, [showToast]);

  const showGameNotification = useCallback((title: string, message: string, options?: { duration?: number; icon?: string }) => {
    return showToast({
      type: 'game',
      title,
      message,
      duration: options?.duration || 3000, // Game notifications remain brief at 3s
      icon: options?.icon
    });
  }, [showToast]);

  const showConnectionAlert = useCallback((title: string, message: string, options?: { duration?: number; icon?: string }) => {
    return showToast({
      type: 'connection',
      title,
      message,
      duration: options?.duration || 5000,
      icon: options?.icon
    });
  }, [showToast]);

  const contextValue: NotificationContextType = {
    showToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showGameNotification,
    showConnectionAlert,
    removeToast,
    clearAllToasts
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
      <ToastContainer 
        toasts={toasts} 
        onRemoveToast={removeToast}
        position={position}
      />
    </NotificationContext.Provider>
  );
};